import React, { useEffect, useMemo, useState } from 'react';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import axios from 'axios';
import {
  fetchSubscriptionData,
  fetchSubscriptionPlanData,
} from '../../api/subscription';
import { useDispatch, useSelector } from 'react-redux';
import {
  setDurationFilter,
  setStatsTypeFilter,
} from '../../containers/app-container/reducer/filterSlice';

const SubscriptionPlans = ({ hideRevenue = false }) => {
  const [plans, setPlans] = useState([]);
  const [subscriptionData, setSubscriptionData] = useState([]);
  // const { filter } = useSelector((state) => state);
  const filter = useSelector((state) => state.filter);
  const filterWithoutUTM = useMemo(() => {
    const { utm_options, searchTerm, ...rest } = filter;
    return rest;
  }, [JSON.stringify(filter)]);

  const fetchData = async () => {
    try {
      fetchSubscriptionPlanData(setPlans);
    } catch (err) {
      console.error('Error while loading subscription plan data', err);
    }
  };

  const fetchSubData = async () => {
    try {
      fetchSubscriptionData(filter, setSubscriptionData);
    } catch (err) {
      console.error('Error while loading subscription plan data', err);
    }
  };

  useEffect(() => {
    fetchData();
    fetchSubData();
  }, [JSON.stringify(filterWithoutUTM)]);
  const selectedDuration = useSelector((state) => state.filter.duration);
  const selectedStatsType = useSelector((state) => state.filter.statsType);
  const dispatch = useDispatch();
  const setSelectedDuration = (value) => {
    dispatch(setDurationFilter({ duration: value }));
  };
  const [hoveredPlan, setHoveredPlan] = useState(null); // State for hovered plan
  // Duration options
  // const durations = ['Monthly', 'Quarterly', 'Annually'];
  const durations = ['Monthly',];

  const subscriptionStatCards = useMemo(() => ([
    {
      title: 'TOTAL SUBSCRIBERS',
      statsType: null,
      statsTitle: 'Subscribed User List',
      color: '#3498db',
      showIcon: true,
      breakdown: subscriptionData?.totalBreakdown,
      fallbackTotal: subscriptionData?.totalSubscribersCount,
    },
    {
      title: 'ACTIVE SUBSCRIBERS',
      statsType: 'subscription_active',
      statsTitle: 'Active Subscribers',
      color: 'var(--success-600)',
      showIcon: true,
      breakdown: subscriptionData?.activeBreakdown,
      fallbackTotal: subscriptionData?.totalActiveSubscribersCount,
    },
    {
      title: 'CANCELLED SUBSCRIBERS',
      statsType: 'subscription_cancelled',
      statsTitle: 'Cancelled Subscribers',
      color: 'var(--danger-400)',
      showIcon: true,
      breakdown: subscriptionData?.cancelBreakdown,
      fallbackTotal: subscriptionData?.totalCancelSubscribersCount,
    },
  ]), [subscriptionData]);

  const handleStatCardClick = (card) => {
    dispatch(setStatsTypeFilter({
      statsType: card.statsType,
      statsTitle: card.statsTitle,
    }));
  };

  const formatPercent = (count, total) => {
    if (!total || !count) return '0.00%';
    return ((count / total) * 100).toFixed(2) + '%';
  };


  return (
    <div>
      {/* Subscription Plans title + Total Revenue summary — hidden on the
          revenue-free Subscription page (hideRevenue), kept on Financials. */}
      {!hideRevenue && (
        <>
          <div className="font-20 text-black fw-semibold mb-10 mt-24">
            Subscription Plans
          </div>

          <div className="d-flex justify-content-between align-items-center mb-12">
            <div className="font-16 fw-semibold text-neutral-500">
              Total Revenue:{' '}
              <span className="font-20 fw-semibold text-black">
                ${subscriptionData.totalAmount ?? 0}
              </span>
            </div>
          </div>
        </>
      )}
      <div className="updatedData">
        {subscriptionStatCards.map((card) => {
          const breakdown = card.breakdown || {};
          const total = breakdown.total ?? card.fallbackTotal ?? 0;
          const maleCount = breakdown.maleCount || 0;
          const femaleCount = breakdown.femaleCount || 0;
          const otherCount = breakdown.otherCount || 0;
          const isActive =
            (selectedStatsType ?? null) === (card.statsType ?? null);

          const rows = [
            {
              label: 'Male',
              count: maleCount,
              percent: formatPercent(maleCount, total),
              isMax: maleCount >= femaleCount && maleCount >= otherCount,
            },
            {
              label: 'Female',
              count: femaleCount,
              percent: formatPercent(femaleCount, total),
              isMax: femaleCount >= maleCount && femaleCount >= otherCount,
            },
            {
              label: 'Other',
              count: otherCount,
              percent: formatPercent(otherCount, total),
              isMax: otherCount >= maleCount && otherCount >= femaleCount,
            },
          ];

          return (
            <div
              key={card.title}
              className="stat-card border"
              role="button"
              onClick={() => handleStatCardClick(card)}
              style={{
                cursor: 'pointer',
                boxShadow: isActive ? '0 0 0 2px #2563eb inset' : 'none',
                transition: 'box-shadow 0.2s ease',
              }}
            >
              <div className="font-14 text-neutral-500 text-uppercase fw-medium d-flex justify-content-start gap-10 align-items-center">
                {card.showIcon && (
                  <i
                    className="ri-circle-fill circle-icon w-auto"
                    style={{ color: card.color || 'black' }}
                  ></i>
                )}
                <div>{card.title}</div>
              </div>
              <div className="fw-medium text-black font-20 font-m text-start py-10">
                {total}
              </div>
              <div className="gender-stats">
                {rows.map((row) => (
                  <div
                    key={row.label}
                    className="d-flex align-items-center flex-wrap gap-10 justify-content-between"
                  >
                    <div className="d-flex align-items-center gap-10 font-12 fw-medium">
                      {row.label}:{' '}
                      <span
                        className={`userPrsnt d-flex align-items-center gap-1 fw-normal ${
                          row.isMax ? 'text-success-500' : 'text-danger-500'
                        }`}
                      >
                        {row.percent}{' '}
                        <iconify-icon
                          icon={
                            row.isMax
                              ? 'meteor-icons:arrow-up-right'
                              : 'meteor-icons:arrow-down-left'
                          }
                          width="16"
                          height="16"
                        ></iconify-icon>
                      </span>
                    </div>
                    <div className="userCount">{row.count}</div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {/* Plans */}
      <div className="GoldPlanDimondPlan d-flex justify-content-between rounded-3 border my-16">
        {plans
          .filter((item) => item.name.includes(filter.duration))
          .map((plan) => (
            <div
              key={plan.id}
              className={`${plan.planType}Plan w-100 p-20 ${plan.planType === 'Gold' ? 'border-end' : ''
                }`}
            >
              <div className="position-relative">
                <button
                  className={`${plan.planType}Plan-btn font-12 text-neutral-900 rounded-pill px-10 d-flex align-items-center gap-2`}
                  onMouseEnter={() => setHoveredPlan(plan.id)}
                  onMouseLeave={() => setHoveredPlan(null)}
                >
                  {plan.name}{' '}
                  <InfoOutlinedIcon sx={{ width: '12px', height: '12px' }} />
                </button>

                {/* Show plan details if this plan is hovered */}
                {hoveredPlan === plan.id && (
                  <div
                    className="Planbg Planbgsetupn1 p-20 rounded-3 border shadow max-w-290-px bg-white"
                    onMouseEnter={() => setHoveredPlan(plan.id)} // Keep showing details when hovering over the details box
                    onMouseLeave={() => setHoveredPlan(null)} // Hide the details when the mouse leaves the details box
                  >
                    {/* Conditional color for Gold Plan */}
                    <div
                      className="Planname text-center font-20 fw-medium"
                      style={{
                        color: plan.planType === 'Gold' ? '#CC9933' : 'inherit', // Gold plan name color
                      }}
                    >
                      {plan?.name}
                    </div>
                    <div className="planPrice text-center font-32 fw-bold text-neutral-900">
                      ${plan.price}{' '}
                      <span className="font-14 fw-medium text-neutral-400 d-block">
                        per month
                      </span>
                    </div>
                    <div className="Planfeatures font-14 fw-medium text-neutral-500 my-12">
                      Features
                    </div>
                    <ul className="PlanFeatuList d-flex justify-content-between align-items-start gap-3 flex-column">
                      {plan.features.map((feature, index) => (
                        <li
                          key={index}
                          className="d-flex gap-1 align-items-start font-14 fw-medium text-neutral-500"
                        >
                          <iconify-icon
                            icon="material-symbols:check-circle"
                            width="16"
                            height="16"
                            style={{
                              position: 'relative',
                              top: '5px',
                              color:
                                plan.planType === 'Gold'
                                  ? '#CC9933'
                                  : '#3C4043', // Change color for Gold plan icon
                            }}
                          ></iconify-icon>{' '}
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>

              {/* Show additional plan details (subscribers, revenue, cancellations) */}
              <div>
                <div className="font-20 fw-medium text-neutral-900">
                  {plan.price}/month
                </div>
                <div className="d-flex justify-content-between">
                  <div className="d-flex align-items-center w-100 gap-10 text-neutral-900 font-14 fw-medium">
                    {plan.subscribers}{' '}
                    <span className="text-neutral-500 font-14 fw-normal">
                      Subscribers
                    </span>
                  </div>
                  <span className="w-100 text-neutral-500 font-14 fw-normal">
                    {plan?.activeSubscribers}
                  </span>
                </div>
                <div className="d-flex justify-content-between">
                  <div className="d-flex align-items-center w-100 gap-10 text-neutral-900 font-14 fw-medium">
                    {/* {plan?.cancelledSubscribers}{' '} */}
                    <span className="text-neutral-500 font-14 fw-normal">
                      Cancellations
                    </span>
                  </div>
                  <span className="w-100 text-neutral-500 font-14 fw-normal">
                    {plan?.cancelledSubscribers}
                  </span>
                </div>
              </div>
            </div>
          ))}
      </div>
    </div>
  );
};

export default SubscriptionPlans;
